import { create } from "zustand";
import type { EngineEvent, RepQuality, RepState } from "@pushup/detection";

export type WorkoutStatus = "idle" | "loading" | "active" | "paused" | "finished" | "error";

export interface WorkoutState {
  status: WorkoutStatus;
  reps: number;
  repState: RepState;
  quality: RepQuality | null;
  lastFeedback: string | null;
  lastFeedbackType: "rep" | "form-error" | null;
  currentDepthDeg: number;
  /** Per-arm elbow bend for diagnostics (null = landmarks not visible). */
  leftElbowDeg: number | null;
  rightElbowDeg: number | null;
  elapsedMs: number;
  errorMessage: string | null;
  /** True once the user lowers into their first pushup (sticky until reset). */
  pushupsStarted: boolean;

  /* ── Celebrity opponent pacing ── */
  celebrityReps: number;
  /** Internal timer ID for celebrity rep pacing — not serialised. */
  _celebrityTimerId: ReturnType<typeof setInterval> | null;

  setStatus: (status: WorkoutStatus) => void;
  setError: (msg: string | null) => void;
  pushEngineEvent: (event: EngineEvent) => void;
  updateDepth: (deg: number) => void;
  updateArmDepths: (left: number | null, right: number | null) => void;
  tickElapsed: (deltaMs: number) => void;
  resetWorkout: () => void;
  setPushupsStarted: (started: boolean) => void;

  /* ── Celebrity pacing actions ── */
  startCelebrityPacing: (secondsPerRep: number) => void;
  stopCelebrityPacing: () => void;
  /** Ends the round (e.g. per-celebrity time limit reached). */
  finishWorkout: () => void;
}

const initialState = {
  status: "idle" as WorkoutStatus,
  reps: 0,
  repState: "waitingForUp" as RepState,
  quality: null as RepQuality | null,
  lastFeedback: null as string | null,
  lastFeedbackType: null as "rep" | "form-error" | null,
  currentDepthDeg: 180,
  leftElbowDeg: null as number | null,
  rightElbowDeg: null as number | null,
  elapsedMs: 0,
  errorMessage: null as string | null,
  pushupsStarted: false,
  celebrityReps: 0,
  _celebrityTimerId: null as ReturnType<typeof setInterval> | null,
};

export const useWorkoutStore = create<WorkoutState>()((set, get) => ({
  ...initialState,

  setStatus: (status) => set({ status }),
  setError: (errorMessage) => set({ errorMessage, status: errorMessage ? "error" : "idle" }),

  pushEngineEvent: (event) =>
    set((state) => {
      if (event.type === "rep") {
        return {
          reps: event.rep,
          quality: event.quality,
          lastFeedback: null,
          lastFeedbackType: null,
        };
      }
      if (event.type === "form-error") {
        return {
          lastFeedback: event.message,
          lastFeedbackType: "form-error",
        };
      }
      if (event.type === "state") {
        return {
          repState: event.state,
        };
      }
      return state;
    }),

  updateDepth: (currentDepthDeg) => set({ currentDepthDeg }),

  updateArmDepths: (leftElbowDeg, rightElbowDeg) => set({ leftElbowDeg, rightElbowDeg }),

  tickElapsed: (deltaMs) => set((state) => ({ elapsedMs: state.elapsedMs + deltaMs })),

  setPushupsStarted: (pushupsStarted) => set({ pushupsStarted }),

  resetWorkout: () => {
    const { _celebrityTimerId } = get();
    if (_celebrityTimerId !== null) {
      clearInterval(_celebrityTimerId);
    }
    set(initialState);
  },

  startCelebrityPacing: (secondsPerRep) => {
    const { _celebrityTimerId } = get();
    if (_celebrityTimerId !== null) {
      clearInterval(_celebrityTimerId);
    }
    const timerId = setInterval(() => {
      set((state) => ({ celebrityReps: state.celebrityReps + 1 }));
    }, secondsPerRep * 1000);
    set({ _celebrityTimerId: timerId, celebrityReps: 0 });
  },

  stopCelebrityPacing: () => {
    const { _celebrityTimerId } = get();
    if (_celebrityTimerId !== null) {
      clearInterval(_celebrityTimerId);
      set({ _celebrityTimerId: null });
    }
  },

  finishWorkout: () => {
    const { _celebrityTimerId } = get();
    if (_celebrityTimerId !== null) {
      clearInterval(_celebrityTimerId);
    }
    set({ _celebrityTimerId: null, status: "finished" });
  },
}));
